import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { FlatList, View, Linking, useWindowDimensions, TouchableOpacity } from 'react-native';
import {
  Poster,
  Gradient,
  Content,
  Hero,
  Container,
  ViewButton,
} from './style';
import Header from '../../components/Header';
import CategoryList from '../../components/CategoryList';
import { doc, setDoc, writeBatch } from 'firebase/firestore';
import { firestore } from '../../services/firebase';
import { firestoreAutoId, removeSansSerif } from '../../utils/validators';
import THEME from '../../config/theme';
import { Title, SubTitle, FooterText } from '../../config/theme/globalStyles';
import { titleMain, subtitleMain } from '../../config/data';
import { SafeAreaView } from 'react-native';
import FotoMain from '../../../assets/FotoMain.jpg';
import Button from '../../components/Button';
import { Provider } from 'react-native-paper';
import { Icon } from 'react-native-elements';
import { useCustomImages } from '../../api/Brand';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import { useAuthUser } from '../../contexts/useAuthUser';
import { useRemoteConfigContext } from '../../contexts/useRemoteConfigContext';
import AdminFiltersModal from '../AdminPanel/Filters';
import AdminCatalogSettingsModal from '../AdminPanel/CatalogSettings';
import TitlePromptModal from '../../components/TitlePromptModal';
import { hasProductAccess, userHasAnyAccess } from '../../utils/accessUtils';
import { ProductsSection } from './ProductsSection';
import { ProductVitrineFilterBar } from './ProductsSection/ProductVitrineFilterBar';
import { useProductVitrineFilters } from './ProductsSection/useProductVitrineFilters';
import { buildBannerSlides } from '../../utils/bannerLinks';
import { useFilters } from '../../queries/useFilters';
import { useFiltersSettings } from '../../queries/useFiltersSettings';
import { useCatalogAccessVisibility } from '../../queries/useCatalogAccessVisibility';
import { shouldShowClearFilter } from '../../utils/filterUtils';
import { useCategories, refreshCategories } from '../../queries/useCategories';
import { FilterBar } from '../../components/FilterBar';
import { persistProductCategoryOrder } from '../../services/productCategoryOrder';
import { queryClient } from '../../queries/queryClient';
import { PRODUCT_CATEGORIES } from '../../constants/entities';
import { useNavigation } from '@react-navigation/native';
import { useFocusEffect } from '@react-navigation/native';
import Carousel, { Pagination } from "react-native-reanimated-carousel";
import { useSharedValue } from "react-native-reanimated";

const MAIN_CAROUSEL_AUTO_PLAY_INTERVAL_MS = 5000;

export function Main({ route }) {

  const navigation = useNavigation();
  const { scrollPosition } = route?.params || {};
  const { width, height } = useWindowDimensions();

  // Categorias e usuário já foram resolvidos no boot (ver `Navigation`): aqui
  // vêm do cache, sem leitura nem loading próprios.
  const { data: allCategoriesAdmin = [], refetch: refetchCategories } =
    useCategories();
  const allCategories = useMemo(
    () => allCategoriesAdmin.filter((category) => !category.archived),
    [allCategoriesAdmin]
  );
  const { user } = useAuthUser();
  const config = useRemoteConfigContext();
  const [visible, setVisible] = useState(false);

  const [modalFilterVisible, setFilterModalVisible] = useState(false);
  const [modalCatalogSettingsVisible, setCatalogSettingsModalVisible] = useState(false);

  const { data: filters = [], isLoading: isFiltersLoading } = useFilters();
  const { data: filterSettings, isLoading: isSettingsLoading } = useFiltersSettings();
  const { restrictToUserAccess } = useCatalogAccessVisibility();
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [didUserSelectFilter, setDidUserSelectFilter] = useState(false);
  const [bypassProductFilter, setBypassProductFilter] = useState(false);
  // Vitrine de Produtos: só o aluno alterna entre ver os produtos e ver o
  // conteúdo (admin sempre vê tudo — é dali que ele gerencia categorias/cards).
  // "Ver todos os conteúdos" força esse modo mesmo sem filtro selecionado.
  const [viewAllContent, setViewAllContent] = useState(false);

  const hasFiltersEnabled = filterSettings != null && !isSettingsLoading;
  const prefilterByTagFirst = filterSettings?.prefilterByTagFirst ?? false;
  const hasRealAccess = userHasAnyAccess(user);

  //adminRole "supportOnly" only allows access to notiications, community and comments
  const hasAdminRole = !user?.adminRole

  const ref = useRef(null);
  const progress = useSharedValue(0);
  const adminViewInitialized = useRef(false);

  const onPressPagination = (index) => {
    ref.current?.scrollTo({
      /**
       * Calculate the difference between the current index and the target index
       * to ensure that the carousel scrolls to the nearest index
       */
      count: index - progress.value,
      animated: true,
    });
  };

  // Admin volta de uma edição de card/categoria: recarrega para ver o resultado.
  useFocusEffect(
    useCallback(() => {
      if (user?.isAdmin) {
        refetchCategories();
      }
    }, [user?.isAdmin, refetchCategories])
  );

  useEffect(() => {
    if (scrollPosition !== undefined) {
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 100);
    }
  }, [scrollPosition]);

  useEffect(() => {
    if (route?.params?.selectedFilter) {
      setSelectedFilter(route.params.selectedFilter);
    }
  }, [route?.params?.selectedFilter]);

  const handleSelectFilter = (value) => {
    if (!hasFiltersEnabled) return;

    setSelectedFilter(value);
    setDidUserSelectFilter(value !== null);
    setViewAllContent(false);

    if (user?.isAdmin) {
      setBypassProductFilter(value === null);
    }
  };

  const handleViewAllContent = () => {
    setSelectedFilter(null);
    setViewAllContent(true);
  };

  const handleShowAllProducts = () => {
    setSelectedFilter(null);
    setViewAllContent(false);
  };

  const handleReorderFilters = useCallback(async (orderedFilters) => {
    const batch = writeBatch(firestore);
    orderedFilters.forEach((filter, index) => {
      if (!filter?.id) return;
      batch.update(doc(firestore, 'filters', filter.id), { order: index + 1 });
    });
    await batch.commit();
  }, []);

  const handleReorderProductCategories = useCallback(async (orderedFilters) => {
    await persistProductCategoryOrder(orderedFilters);
    queryClient.invalidateQueries({ queryKey: [PRODUCT_CATEGORIES] });
  }, []);

  const wantsProductVitrine = config.hasProductCatalog && !selectedFilter && !viewAllContent;

  const showModal = () => setVisible(true);

  useEffect(() => {
    if (user?.isAdmin) {
      setBypassProductFilter(true);
      if (!adminViewInitialized.current) {
        setViewAllContent(true);
        adminViewInitialized.current = true;
      }
    }
  }, [user?.isAdmin]);

  const {
    customColor: customTheme,
    customInfo,
    customFont,
  } = useCustomTheme();

  const productVitrine = useProductVitrineFilters({
    user,
    enabled: wantsProductVitrine,
    defaultToMine: !user?.isAdmin,
  });

  // Showcase on but nothing to show (no product with cover + category, or a
  // student whose access comes only from items outside the showcase): fall
  // back to the content list instead of rendering an empty Home.
  const showProductsInsteadOfContent = wantsProductVitrine && !productVitrine.isEmpty;
  const productsSectionEnabled = showProductsInsteadOfContent;
  const vitrineIsEntry =
    config.hasProductCatalog && !viewAllContent && !productVitrine.isEmpty;

  useEffect(() => {
    if (isSettingsLoading || isFiltersLoading || !user) return;
    if (!hasFiltersEnabled) return;
    if (selectedFilter !== null) return;
    // With the showcase as the entry point the pre-filter only applies after
    // "Ver todos os conteúdos" (or when the showcase has nothing to show);
    // otherwise it re-selected the first tag and undid "Ver todos os produtos".
    if (vitrineIsEntry) return;

    if (prefilterByTagFirst && filters.length > 0 && !user?.isAdmin) {
      setSelectedFilter(filters[0].value);
      setBypassProductFilter(false);
    }
  }, [isSettingsLoading, isFiltersLoading, hasFiltersEnabled, selectedFilter, filters, prefilterByTagFirst, user, vitrineIsEntry]);

  useEffect(() => {
    if (isSettingsLoading || isFiltersLoading || user?.isAdmin) return;
    if (!hasFiltersEnabled) return;
    if (selectedFilter !== null) return;
    // With the showcase as the entry point the pre-filter only applies after
    // "Ver todos os conteúdos" (or when the showcase has nothing to show);
    // otherwise it re-selected the first tag and undid "Ver todos os produtos".
    if (vitrineIsEntry) return;

    const isAnonOrNoAccess = !user || !hasRealAccess;

    if (isAnonOrNoAccess && prefilterByTagFirst && filters.length > 0) {
      setSelectedFilter(filters[0].value);
      setBypassProductFilter(false);
    }
  }, [
    isSettingsLoading,
    isFiltersLoading,
    hasFiltersEnabled,
    selectedFilter,
    filters,
    prefilterByTagFirst,
    user,
    hasRealAccess,
    vitrineIsEntry,
  ]);



  const filterBarFonts =
    customFont && customFont.fontFamilyText
      ? removeSansSerif(customFont.fontFamilyText)
      : THEME.FONTFAMILY.REGULAR;

  const renderBannerFilters = () => {
    const productCatalogActions = config.hasProductCatalog && !productVitrine.isEmpty
      ? [{ label: 'Ver todos os produtos', onPress: handleShowAllProducts }]
      : undefined;

    if (showProductsInsteadOfContent && productVitrine.visibleProducts.length > 0) {
      return (
        <ProductVitrineFilterBar
          filters={productVitrine.filters}
          selected={productVitrine.effectiveFilter}
          onSelect={productVitrine.setSelectedFilter}
          onViewAllContent={handleViewAllContent}
          onReorderCategories={
            user?.isAdmin ? handleReorderProductCategories : undefined
          }
          colors={{
            activeButton: customTheme?.button?.backgroundApp ?? THEME.COLORS.PRIMARY_800,
            inactiveButton: customTheme?.iconsMain?.icons ?? THEME.COLORS.ICON_MAIN,
            activeText: customTheme?.button?.textApp ?? THEME.COLORS.TEXT_BUTTON,
            background: customTheme?.background?.app ?? THEME.COLORS.BACKGROUND_MAIN,
            text: customTheme?.text?.app ?? THEME.COLORS.TEXT_MAIN,
          }}
          fonts={filterBarFonts}
          overlay
        />
      );
    }

    if (hasFiltersEnabled && !isFiltersLoading && filters.length > 0 && !showProductsInsteadOfContent) {
      return (
        <>
          <FilterBar
            filters={validFilters}
            selected={selectedFilter}
            onSelect={handleSelectFilter}
            onReorder={user?.isAdmin ? handleReorderFilters : undefined}
            colors={
              customTheme?.iconsMain?.icons || THEME.COLORS.ICON_MAIN
            }
            fonts={filterBarFonts}
            showClear={shouldShowClearFilter({
              hasRealAccess,
              prefilterByTagFirst,
              didUserSelectFilter,
              selectedFilter,
              isAdmin: !!user?.isAdmin,
            })}
            overlay
            extraMenuActions={productCatalogActions}
          />
        </>
      );
    }

    if (
      config.hasProductCatalog &&
      !showProductsInsteadOfContent &&
      user?.isAdmin
    ) {
      return (
        <FilterBar
          filters={[]}
          selected={null}
          onSelect={() => {}}
          colors={
            customTheme?.iconsMain?.icons || THEME.COLORS.ICON_MAIN
          }
          fonts={filterBarFonts}
          showClear={false}
          overlay
          extraMenuActions={productCatalogActions}
        />
      );
    }

    return null;
  };

  const categories = [
    {
      title: 'NOME CATEGORIA',
      order: allCategoriesAdmin.length + 1,
      archived: true,
      cards: [
        {
          title: 'Nome do Módulo',
          description: '',
          order: 1,
          archived: true,
        },
      ],
    },
  ];

  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);

  // Título agora é OBRIGATÓRIO na criação (via TitlePromptModal), com opção de
  // ocultar do aluno mantendo-o para busca/filtros/acesso. `hideTitle` é gravado
  // na categoria e no primeiro módulo criado junto.
  const firestoreAddCategory = async (title, hideTitle) => {
    const firestoreId = firestoreAutoId();
    const categoriesCollectionRef = doc(firestore, 'categories', firestoreId);
    await setDoc(
      categoriesCollectionRef,
      {
        title,
        hideTitle: !!hideTitle,
        order: categories[0].order,
        archived: categories[0].archived,
      },
      { merge: true }
    );
    const cardsRef = doc(
      firestore,
      'categories',
      firestoreId,
      'cards',
      firestoreAutoId()
    );
    await setDoc(
      cardsRef,
      {
        // Primeiro módulo herda o título/visibilidade da categoria (em vez do
        // "Nome do Módulo" padrão); o admin renomeia depois se quiser.
        title,
        hideTitle: !!hideTitle,
        description: categories[0].cards[0].description,
        order: categories[0].cards[0].order,
        archived: categories[0].cards[0].archived,
      },
      { merge: true }
    );
    refreshCategories();
  };

  function hexToRgba(hex, alpha = 0.3) {
    const cleanHex = hex.replace('#', '');

    const r = parseInt(cleanHex.slice(0, 2), 16);
    const g = parseInt(cleanHex.slice(2, 4), 16);
    const b = parseInt(cleanHex.slice(4, 6), 16);

    return `rgba(${r},${g},${b},${alpha})`;
  }

  const hexColor = customTheme && customTheme.text && customTheme.text.app
    ? customTheme.text.app
    : THEME.COLORS.TEXT_MAIN

  const paginationColor = useMemo(
    () => hexToRgba(hexColor),
    []
  );

  // Já resolvida no boot (ver Navigation), então aqui vem do cache do
  // react-query e não precisa de loading próprio.
  const { data: imageMain } = useCustomImages('imageApp');

  // Imagem + link de cada slide, casados pela CHAVE DO SLOT (ver
  // `utils/bannerLinks`). Cruzar as duas listas por posição quebrava o
  // redirecionamento assim que um slot do meio era apagado.
  const bannerSlides = useMemo(() => buildBannerSlides(imageMain), [imageMain]);

  const urlsArray = bannerSlides.map((slide) => slide.url);

  const filteredCategories = (user?.isAdmin && bypassProductFilter)
    ? allCategoriesAdmin.map((category) => ({
      ...category,
      cards: category.cards.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    }))
    : allCategories.map((category) => {
      let visibleCards = category.cards || [];

      if (restrictToUserAccess && !user?.isAdmin && !bypassProductFilter) {
        visibleCards = visibleCards.filter((card) => hasProductAccess(card, user));
      }

      if (hasFiltersEnabled && selectedFilter) {
        if (category.filterIds?.includes(selectedFilter)) {
          // mantém todos os cards
        } else {
          visibleCards = visibleCards.filter((card) =>
            card.filterIds?.includes(selectedFilter)
          );
        }
      }

      const finalCards = visibleCards.filter((card) => {
        if (user?.isAdmin && bypassProductFilter) return true;
        return !card.archived && !card.hideContent;
      });

      return {
        ...category,
        cards: finalCards.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      };
    })
      .filter((c) => c.cards?.length > 0);


  // Filtra os filtros que realmente possuem conteúdo visível
  const validFilters = user?.isAdmin
    ? filters
    : filters.filter((filter) => {
      const match = allCategories.some((category) => {
        let visibleCards = category.cards || [];

        if (
          restrictToUserAccess &&
          !user?.isAdmin &&
          !bypassProductFilter
        ) {
          visibleCards = visibleCards.filter((card) =>
            hasProductAccess(card, user)
          );
        }

        if (category.filterIds?.includes(filter.value)) {
          return visibleCards.length > 0;
        } else {
          return visibleCards.some((card) =>
            card.filterIds?.includes(filter.value)
          );
        }
      });

      return match;
    });

  const footer = (
    <>
      <View
        style={{
          height: 0.9,
          width: '100%',
          backgroundColor:
            customTheme && customTheme.text && customTheme.text.app
              ? customTheme.text.app
              : THEME.COLORS.TEXT_MAIN,
          marginTop: '3.5rem',
        }}
      />
      <View
        style={{
          width: '100%',
          backgroundColor:
            customTheme && customTheme.background && customTheme.background.app
              ? customTheme.background.app
              : THEME.COLORS.BACKGROUND_MAIN,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          paddingVertical: '1rem',
        }}
      >
        <FooterText
          padding="0rem 0.2rem"
          style={{
            fontFamily:
              customFont && customFont.fontFamilyText
                ? customFont.fontFamilyText
                : THEME.FONTFAMILY.LIGHT,
          }}
          color={
            customTheme && customTheme.text && customTheme.text.app
              ? customTheme.text.app
              : THEME.COLORS.TEXT_MAIN
          }
        >
          Desenvolvido com
        </FooterText>
        <Icon
          type="material-community"
          name="heart"
          color={
            customTheme && customTheme.text && customTheme.text.app
              ? customTheme.text.app
              : THEME.COLORS.TEXT_MAIN
          }
          size="12px"
        />
        <FooterText
          padding="0rem 0.2rem"
          style={{
            fontFamily:
              customFont && customFont.fontFamilyText
                ? customFont.fontFamilyText
                : THEME.FONTFAMILY.LIGHT,
          }}
          color={
            customTheme && customTheme.text && customTheme.text.app
              ? customTheme.text.app
              : THEME.COLORS.TEXT_MAIN
          }
        >
          pela
        </FooterText>
        <FooterText
          style={{
            fontFamily:
              customFont && customFont.fontFamilyTitle
                ? customFont.fontFamilyTitle
                : THEME.FONTFAMILY.BOLD,
          }}
          color={
            customTheme && customTheme.text && customTheme.text.app
              ? customTheme.text.app
              : THEME.COLORS.TEXT_MAIN
          }
          padding="0rem 0.3rem"
          onPress={() =>
            Linking.openURL('https://www.instagram.com/blix.tecnologia/')
          }
        >
          BLIX
        </FooterText>
      </View>
    </>
  );

  const onPressBanner = (index) => {
    const url = bannerSlides[index]?.link;
    if (!url) return;

    // `openURL` rejeita quando não há aplicativo para o esquema (link com erro
    // de digitação, por exemplo). Sem o catch, a promise rejeitada aparece como
    // erro não tratado no console e não diz nada de útil.
    Linking.openURL(url).catch((error) =>
      console.error(`Não foi possível abrir o link do banner (${url}):`, error)
    );
  };

  // Camada clicável do banner de UMA imagem só (sem carrossel).
  //
  // Nesse caminho a foto não passava por nenhum Touchable, então o link da
  // imagem 1 simplesmente não existia: bastava ter um único banner para o
  // redirecionamento nunca acontecer.
  //
  // Ela é o PRIMEIRO filho do gradiente, logo fica atrás do header, dos filtros
  // e dos textos em destaque — que são desenhados depois e continuam recebendo
  // os próprios toques. O link responde só nas áreas livres da foto.
  const renderSingleBannerLink = () =>
    bannerSlides[0]?.link ? (
      <TouchableOpacity
        accessibilityRole="link"
        onPress={() => onPressBanner(0)}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      />
    ) : null;

  return user?.isAdmin ? (
    <Provider>
      <SafeAreaView
        style={{
          flexGrow: 1,
          backgroundColor:
            customTheme && customTheme.background && customTheme.background.app
              ? customTheme.background.app
              : THEME.COLORS.BACKGROUND_MAIN,
        }}
      >
        <Container
          background={
            customTheme && customTheme.background && customTheme.background.app
              ? customTheme.background.app
              : THEME.COLORS.BACKGROUND_MAIN
          }
        >
          {Array.isArray(urlsArray) && urlsArray?.length > 1 && typeof imageMain?.url !== 'string' ?
            <>
              <View
                id="carousel-component"
                dataSet={{ kind: "basic-layouts", name: "parallax" }}
                style={{
                  backgroundColor: customTheme && customTheme.background && customTheme.background.app
                    ? customTheme.background.app
                    : THEME.COLORS.BACKGROUND_MAIN,
                  width: '100%',
                  height: (height * 0.70), // mesmo tamanho do Poster
                  position: 'relative',
                }}
              >
                <Carousel
                  ref={ref}
                  autoPlay
                  autoPlayInterval={MAIN_CAROUSEL_AUTO_PLAY_INTERVAL_MS}
                  scrollAnimationDuration={800}
                  data={urlsArray}
                  loop={true}
                  pagingEnabled={true}
                  snapEnabled={true}
                  height={height * 0.70} // mesmo tamanho do Poster
                  width={width}
                  mode="parallax"
                  modeConfig={{
                    parallaxScrollingScale: 1,
                    parallaxScrollingOffset: 0,
                  }}
                  onProgressChange={progress}
                  renderItem={({ index }) => (
                    // Slide = SÓ a foto de fundo + gradiente. Header/filtros/textos
                    // ficam num overlay FIXO fora do carrossel (não rolam).
                    <TouchableOpacity
                      pointerEvents="box-only"
                      disabled={!bannerSlides[index]?.link}
                      onPress={() =>
                        onPressBanner(index)
                      }
                    >
                      <Poster
                        source={urlsArray?.[index]}
                      >
                        <Gradient
                          locations={[0, 0.2, 0.7, 1]}
                          colors={
                            customTheme &&
                              customTheme.background &&
                              customTheme.background.app
                              ? [
                                'rgba(0,0,0,0.5)',
                                'rgba(0,0,0,0.1)',
                                'rgba(0,0,0,0.1)',
                                `${customTheme.background.app}`,
                              ]
                              : THEME.COLORS.GRADIENT_MAIN
                          }
                        />
                      </Poster>
                    </TouchableOpacity>
                  )}
                />

                {/* Overlay FIXO sobre o carrossel: header, filtros e textos não
                    rolam com a foto. `box-none` deixa o toque em áreas vazias
                    chegar ao banner (navegação por slide) e mantém header/filtros
                    clicáveis. */}
                <View
                  pointerEvents="box-none"
                  style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
                >
                  <Header
                    navigation={navigation}
                    user={user}
                    isPageMain={true}
                    onOpenFilters={() => setFilterModalVisible(true)}
                    onOpenCatalogSettings={() => setCatalogSettingsModalVisible(true)}
                  />
                  {renderBannerFilters()}
                  <Hero>
                    {/* `customInfo` já foi resolvido no boot (ver Navigation),
                        então o título entra direto — não precisa de spinner. */}
                    <Title
                      color={
                        customTheme &&
                          customTheme.text &&
                          customTheme.text.titleMain
                          ? customTheme.text.titleMain
                          : THEME.COLORS.TITLE_OVERPHOTO_MAIN
                      }
                      style={{
                        fontFamily:
                          customFont && customFont.fontFamilyTitle
                            ? customFont.fontFamilyTitle
                            : THEME.FONTFAMILY.BOLD,
                      }}
                      textAlign="left"
                      margin="0.5rem 0rem"
                    >
                      {customInfo && customInfo.title
                        ? customInfo.title
                        : titleMain}
                    </Title>
                    <SubTitle
                      color={
                        customTheme &&
                          customTheme.text &&
                          customTheme.text.titleMain
                          ? customTheme.text.titleMain
                          : THEME.COLORS.TITLE_OVERPHOTO_MAIN
                      }
                      style={{
                        fontFamily:
                          customFont && customFont.fontFamilySubtitle
                            ? customFont.fontFamilySubtitle
                            : THEME.FONTFAMILY.MEDIUM,
                      }}
                      textAlign="left"
                    >
                      {customInfo && customInfo.subTitle
                        ? customInfo.subTitle
                        : subtitleMain}
                    </SubTitle>
                  </Hero>
                </View>
              </View>

              {urlsArray?.length > 1 &&
                <Pagination.Basic
                  progress={progress}
                  data={urlsArray}
                  dotStyle={{
                    backgroundColor: paginationColor, borderRadius: 50,
                  }}
                  activeDotStyle={{
                    backgroundColor: customTheme && customTheme.text && customTheme.text.app
                      ? customTheme.text.app
                      : THEME.COLORS.TEXT_MAIN
                  }}
                  containerStyle={{ gap: 5, marginTop: 10 }}
                  onPress={onPressPagination}
                />
              }
            </>
            :
            (
              <Poster source={urlsArray?.[0] || (imageMain ? imageMain?.url : FotoMain)}>
                <Gradient
                  locations={[0, 0.2, 0.7, 1]}
                  colors={
                    customTheme &&
                      customTheme.background &&
                      customTheme.background.app
                      ? [
                        'rgba(0,0,0,0.5)',
                        'rgba(0,0,0,0.1)',
                        'rgba(0,0,0,0.1)',
                        `${customTheme.background.app}`,
                      ]
                      : THEME.COLORS.GRADIENT_MAIN
                  }
                >
                  {renderSingleBannerLink()}
                  <Header
                    navigation={navigation}
                    user={user}
                    isPageMain={true}
                    onOpenFilters={() => setFilterModalVisible(true)}
                    onOpenCatalogSettings={() => setCatalogSettingsModalVisible(true)}
                  />
                  {renderBannerFilters()}
                  <Hero>
                    {/* `customInfo` já foi resolvido no boot (ver Navigation),
                        então o título entra direto — não precisa de spinner. */}
                    <Title
                      color={
                        customTheme &&
                          customTheme.text &&
                          customTheme.text.titleMain
                          ? customTheme.text.titleMain
                          : THEME.COLORS.TITLE_OVERPHOTO_MAIN
                      }
                      style={{
                        fontFamily:
                          customFont && customFont.fontFamilyTitle
                            ? customFont.fontFamilyTitle
                            : THEME.FONTFAMILY.BOLD,
                      }}
                      textAlign="left"
                      margin="0.5rem 0rem"
                    >
                      {customInfo && customInfo.title
                        ? customInfo.title
                        : titleMain}
                    </Title>
                    <SubTitle
                      color={
                        customTheme &&
                          customTheme.text &&
                          customTheme.text.titleMain
                          ? customTheme.text.titleMain
                          : THEME.COLORS.TITLE_OVERPHOTO_MAIN
                      }
                      style={{
                        fontFamily:
                          customFont && customFont.fontFamilySubtitle
                            ? customFont.fontFamilySubtitle
                            : THEME.FONTFAMILY.MEDIUM,
                      }}
                      textAlign="left"
                    >
                      {customInfo && customInfo.subTitle
                        ? customInfo.subTitle
                        : subtitleMain}
                    </SubTitle>
                  </Hero>
                </Gradient>
              </Poster>
            )
          }
          <ProductsSection
            user={user}
            enabled={productsSectionEnabled}
            showProductGrid={showProductsInsteadOfContent}
            onViewAllContent={handleViewAllContent}
            vitrine={productVitrine}
            hideFilterBar
          />
          {!showProductsInsteadOfContent && (
          <Content
            style={{
              background:
                customTheme &&
                  customTheme.background &&
                  customTheme.background.app
                  ? customTheme.background.app
                  : THEME.COLORS.BACKGROUND_MAIN,
            }}
          >
            <FlatList
              data={filteredCategories}
              showsHorizontalScrollIndicator={false}
              keyExtractor={item => item.id}
              renderItem={({ item }) => (
                <CategoryList
                  category={item}
                  modalFunction={showModal}
                  navigation={navigation}
                  user={user}
                  selectedFilter={selectedFilter}
                  onRefreshCategories={refreshCategories}
                />
              )}
            />
          </Content>
          )}
          {hasAdminRole && !showProductsInsteadOfContent &&
            <ViewButton>
              <Button
                title={'+ NOVA CATEGORIA'}
                onPress={() => setShowNewCategoryModal(true)}
                fontFamily={
                  customFont && customFont.fontFamilyText
                    ? removeSansSerif(customFont.fontFamilyText)
                    : THEME.FONTFAMILY.REGULAR
                }
                fontSize={14}
                borderRadius={10}
              ></Button>
            </ViewButton>
          }
          <TitlePromptModal
            visible={showNewCategoryModal}
            entityLabel="categoria"
            onCancel={() => setShowNewCategoryModal(false)}
            onConfirm={async (title, hideTitle) => {
              setShowNewCategoryModal(false);
              await firestoreAddCategory(title, hideTitle);
            }}
          />
          {footer}
        </Container>
        <AdminCatalogSettingsModal
          visible={modalCatalogSettingsVisible}
          onClose={() => setCatalogSettingsModalVisible(false)}
          onSaved={refreshCategories}
        />
        <AdminFiltersModal
          visible={modalFilterVisible}
          onClose={() => setFilterModalVisible(false)}
          onRefreshCategories={refreshCategories}
        />
      </SafeAreaView>
    </Provider >
  ) : (
    <SafeAreaView
      style={{
        flexGrow: 1,
        backgroundColor:
          customTheme && customTheme.background && customTheme.background.app
            ? customTheme.background.app
            : THEME.COLORS.BACKGROUND_MAIN,
      }}
    >
      <Container
        background={
          customTheme && customTheme.background && customTheme.background.app
            ? customTheme.background.app
            : THEME.COLORS.BACKGROUND_MAIN
        }
      >
        {Array.isArray(urlsArray) && urlsArray?.length > 1 && typeof imageMain?.url !== 'string' ?
          <>
            <View
              id="carousel-component"
              dataSet={{ kind: "basic-layouts", name: "parallax" }}
              style={{
                backgroundColor: customTheme && customTheme.background && customTheme.background.app
                  ? customTheme.background.app
                  : THEME.COLORS.BACKGROUND_MAIN,
                width: '100%',
                height: (height * 0.70), // mesmo tamanho do Poster
                position: 'relative',
              }}
            >
              <Carousel
                ref={ref}
                autoPlayInterval={MAIN_CAROUSEL_AUTO_PLAY_INTERVAL_MS}
                data={urlsArray}
                loop={true}
                pagingEnabled={true}
                snapEnabled={true}
                height={height * 0.70} // mesmo tamanho do Poster
                width={width}
                mode="parallax"
                autoPlay
                scrollAnimationDuration={800}
                modeConfig={{
                  parallaxScrollingScale: 1,
                  parallaxScrollingOffset: 0,
                }}
                onProgressChange={progress}
                renderItem={({ index }) => (
                  // Slide = SÓ a foto de fundo + gradiente. Header/filtros/textos
                  // ficam num overlay FIXO fora do carrossel (não rolam).
                  <TouchableOpacity
                    pointerEvents="box-only"
                    disabled={!bannerSlides[index]?.link}
                    onPress={() =>
                      onPressBanner(index)
                    }
                  >
                    <Poster
                      source={urlsArray?.[index]}
                    >
                      <Gradient
                        locations={[0, 0.2, 0.7, 1]}
                        colors={
                          customTheme &&
                            customTheme.background &&
                            customTheme.background.app
                            ? [
                              'rgba(0,0,0,0.5)',
                              'rgba(0,0,0,0.1)',
                              'rgba(0,0,0,0.1)',
                              `${customTheme.background.app}`,
                            ]
                            : THEME.COLORS.GRADIENT_MAIN
                        }
                      />
                    </Poster>
                  </TouchableOpacity>
                )}
              />

              {/* Overlay FIXO sobre o carrossel: header, filtros e textos não
                  rolam com a foto. `box-none` deixa o toque em áreas vazias
                  chegar ao banner (navegação por slide) e mantém header/filtros
                  clicáveis. */}
              <View
                pointerEvents="box-none"
                style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
              >
                <Header
                  navigation={navigation}
                  user={user}
                  isPageMain={true}
                  onOpenFilters={() => setFilterModalVisible(true)}
                  onOpenCatalogSettings={() => setCatalogSettingsModalVisible(true)}
                />
                {renderBannerFilters()}
                <Hero>
                  <Title
                    color={
                      customTheme && customTheme.text && customTheme.text.titleMain
                        ? customTheme.text.titleMain
                        : THEME.COLORS.TITLE_OVERPHOTO_MAIN
                    }
                    style={{
                      fontFamily:
                        customFont && customFont.fontFamilyTitle
                          ? customFont.fontFamilyTitle
                          : THEME.FONTFAMILY.BOLD,
                    }}
                    textAlign="left"
                    margin="0.5rem 0rem"
                  >
                    {customInfo && customInfo.title ? customInfo.title : titleMain}
                  </Title>
                  <SubTitle
                    color={
                      customTheme && customTheme.text && customTheme.text.titleMain
                        ? customTheme.text.titleMain
                        : THEME.COLORS.TITLE_OVERPHOTO_MAIN
                    }
                    style={{
                      fontFamily:
                        customFont && customFont.fontFamilySubtitle
                          ? customFont.fontFamilySubtitle
                          : THEME.FONTFAMILY.MEDIUM,
                    }}
                    textAlign="left"
                  >
                    {customInfo && customInfo.subTitle
                      ? customInfo.subTitle
                      : subtitleMain}
                  </SubTitle>
                </Hero>
              </View>
            </View>

            {urlsArray?.length > 1 &&
              <Pagination.Basic
                progress={progress}
                data={urlsArray}
                dotStyle={{
                  backgroundColor: paginationColor, borderRadius: 50,
                }}
                activeDotStyle={{
                  backgroundColor: customTheme && customTheme.text && customTheme.text.app
                    ? customTheme.text.app
                    : THEME.COLORS.TEXT_MAIN
                }}
                containerStyle={{ gap: 5, marginTop: 10 }}
                onPress={onPressPagination}
              />
            }
          </>
          :
          (
            <Poster source={urlsArray?.[0] || (imageMain ? imageMain?.url : FotoMain)}>
              <Gradient
                locations={[0, 0.2, 0.7, 1]}
                colors={
                  customTheme &&
                    customTheme.background &&
                    customTheme.background.app
                    ? [
                      'rgba(0,0,0,0.5)',
                      'rgba(0,0,0,0.1)',
                      'rgba(0,0,0,0.1)',
                      `${customTheme.background.app}`,
                    ]
                    : THEME.COLORS.GRADIENT_MAIN
                }
              >
                {renderSingleBannerLink()}
                <Header
                  navigation={navigation}
                  user={user}
                  isPageMain={true}
                  onOpenFilters={() => setFilterModalVisible(true)}
                  onOpenCatalogSettings={() => setCatalogSettingsModalVisible(true)}
                />
                {renderBannerFilters()}
                <Hero>
                  <Title
                    color={
                      customTheme && customTheme.text && customTheme.text.titleMain
                        ? customTheme.text.titleMain
                        : THEME.COLORS.TITLE_OVERPHOTO_MAIN
                    }
                    style={{
                      fontFamily:
                        customFont && customFont.fontFamilyTitle
                          ? customFont.fontFamilyTitle
                          : THEME.FONTFAMILY.BOLD,
                    }}
                    textAlign="left"
                    margin="0.5rem 0rem"
                  >
                    {customInfo && customInfo.title ? customInfo.title : titleMain}
                  </Title>
                  <SubTitle
                    color={
                      customTheme && customTheme.text && customTheme.text.titleMain
                        ? customTheme.text.titleMain
                        : THEME.COLORS.TITLE_OVERPHOTO_MAIN
                    }
                    style={{
                      fontFamily:
                        customFont && customFont.fontFamilySubtitle
                          ? customFont.fontFamilySubtitle
                          : THEME.FONTFAMILY.MEDIUM,
                    }}
                    textAlign="left"
                  >
                    {customInfo && customInfo.subTitle
                      ? customInfo.subTitle
                      : subtitleMain}
                  </SubTitle>
                </Hero>
              </Gradient>
            </Poster>
          )
        }
        <ProductsSection
          user={user}
          enabled={productsSectionEnabled}
          showProductGrid={showProductsInsteadOfContent}
          onViewAllContent={handleViewAllContent}
          vitrine={productVitrine}
          hideFilterBar
        />
        {!showProductsInsteadOfContent && (
        <Content
          style={{
            background:
              customTheme &&
                customTheme.background &&
                customTheme.background.app
                ? customTheme.background.app
                : THEME.COLORS.BACKGROUND_MAIN,
          }}
        >
          <FlatList
            data={filteredCategories}
            showsHorizontalScrollIndicator={false}
            keyExtractor={item => item.id}
            // Mesmo espaçamento vertical que já existe entre o título de cada
            // categoria e seus módulos (CategoryList usa paddingVertical: 20) —
            // sem isto, o filtro (quando visível) ficava colado no título da
            // 1ª categoria.
            contentContainerStyle={{ paddingTop: 20 }}
            renderItem={({ item }) => (
              <CategoryList
                category={item}
                user={user}
                selectedFilter={selectedFilter}
              />
            )}
          />
        </Content>
        )}
        {footer}
      </Container>
    </SafeAreaView>
  );
}
